import type React from "react";

/**
 * Shows a message at the pointer position. Which is nice for an easy feedback mechanism.
 * @param message The message to show.
 * @param event The event used to position the message.
 *
 * @example
 * const copyAsJson = (event: React.MouseEvent) => {
 *   const json = JSON.stringify(value);
 *   navigator.clipboard.writeText(json);
 *   showMessageAtPointer("Copied as JSON", event);
 * };
 */
export const showMessageAtPointer = (message: string, event?: React.MouseEvent | MouseEvent) => {
  // Get position from event or fallback to center of screen
  const x = event?.clientX ?? window.innerWidth / 2;
  const y = event?.clientY ?? window.innerHeight / 2;

  // Guided by @docs/ui-style-guidelines-tailwind-only.md
  const baseClasses =
    "fixed pointer-events-none z-[9999] bg-black/90 text-white px-3 py-1.5 rounded-sm text-xs font-mono shadow-sm backdrop-blur-sm border border-white/20";

  const pointer = document.createElement("div");
  pointer.textContent = message;
  pointer.className = `${baseClasses} transition-all duration-300 ease-out opacity-100 translate-y-0`;
  pointer.style.cssText = `left: ${x}px; top: ${y - 35}px; transform: translateX(-50%);`;

  document.body.appendChild(pointer);

  // Start fade and float animation
  setTimeout(() => {
    pointer.className = `${baseClasses} transition-all duration-500 ease-in opacity-0 -translate-y-4`;
  }, 200);

  // Remove after animation completes
  setTimeout(() => pointer.remove(), 700);
};
